import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../utils/api';

/** Worker session timeout check interval (5 min). */
const WORKER_TIMEOUT_CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Default session timeout in hours when vineyard config is unavailable. */
const DEFAULT_SESSION_TIMEOUT_HOURS = 16;
const READ_ONLY_STORAGE_KEY = 'vb_is_read_only';

/** Roles that can access manager-only features (Crew Control, worker management, time export, etc.). */
const MANAGEMENT_ROLES = ['owner', 'admin', 'manager'] as const;

/** True if the user can access manager-only features (is_superuser or has an owner/admin/manager role). */
function canAccessManagerFeatures(user: User | null): boolean {
  return !!(user?.is_superuser || (user?.role && MANAGEMENT_ROLES.includes(user.role as typeof MANAGEMENT_ROLES[number])));
}

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string | null;
  is_active: boolean;
  is_superuser: boolean;
  is_read_only?: boolean;
  role?: string; // Single role in single vineyard (vineyard isolation)
  vineyard_id?: string;
  organization_type?: 'vineyard' | 'orchard' | 'greenhouse';
  created_at: string;
  updated_at: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  /** True if user can access manager-only features (Crew Control, Workers, Time Export, Manager Tasks). Use this instead of duplicating role checks. */
  canAccessManagerFeatures: boolean;
  login: (username: string, password: string) => Promise<User>;
  register: (userData: RegisterData) => Promise<void>;
  /** Call with no args for manual logout; use 'session_timeout' only when auto-logging out so backend can auto-complete tasks. */
  logout: (reason?: 'session_timeout') => void;
}

interface RegisterData {
  email: string;
  username: string;
  password: string;
  full_name: string;
  acceptTerms?: boolean;
  organization_type?: 'vineyard' | 'orchard' | 'greenhouse';
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always try to fetch the user once (supports cookie-auth sessions too).
    fetchUser();
  }, []);

  const syncReadOnlyStorage = useCallback((nextUser: User | null) => {
    if (nextUser?.is_read_only) {
      localStorage.setItem(READ_ONLY_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(READ_ONLY_STORAGE_KEY);
    }
  }, []);

  const logout = useCallback((reason?: 'session_timeout') => {
    // Best-effort server logout to clear HttpOnly auth cookies.
    // Pass reason: 'session_timeout' only for auto-logout so backend auto-completes tasks and clocks out.
    api.post('/api/auth/logout', reason ? { reason } : undefined).catch(() => undefined);
    localStorage.removeItem('worker_login_time'); // Clear worker login time
    localStorage.removeItem(READ_ONLY_STORAGE_KEY);
    setUser(null);
  }, []);

  // Worker auto-logout: check if worker session has exceeded timeout
  useEffect(() => {
    if (!user || user.role !== 'worker') {
      return; // Only apply to workers
    }

    const triggerSessionTimeoutLogout = (timeoutHours: number) => {
      alert(`Your session has expired after ${timeoutHours} hours. Please log in again.`);
      logout('session_timeout');
    };

    const checkWorkerSessionTimeout = async () => {
      try {
        const loginTimeStr = localStorage.getItem('worker_login_time');
        if (!loginTimeStr) {
          localStorage.setItem('worker_login_time', Date.now().toString());
          return;
        }

        const loginTime = parseInt(loginTimeStr, 10);
        if (Number.isNaN(loginTime) || loginTime <= 0) {
          localStorage.setItem('worker_login_time', Date.now().toString());
          return;
        }

        const now = Date.now();
        const sessionDurationMs = now - loginTime;

        try {
          const response = await api.get('/api/vineyard/info');
          const vineyardInfo = response.data;
          const timeoutHours = vineyardInfo.worker_session_timeout_hours ?? DEFAULT_SESSION_TIMEOUT_HOURS;
          const timeoutMs = timeoutHours * 60 * 60 * 1000;

          if (sessionDurationMs >= timeoutMs) {
            triggerSessionTimeoutLogout(timeoutHours);
          }
        } catch (error) {
          console.error('Error fetching vineyard info for timeout check:', error);
          const defaultTimeoutMs = DEFAULT_SESSION_TIMEOUT_HOURS * 60 * 60 * 1000;
          if (sessionDurationMs >= defaultTimeoutMs) {
            triggerSessionTimeoutLogout(DEFAULT_SESSION_TIMEOUT_HOURS);
          }
        }
      } catch (error) {
        console.error('Error checking worker session timeout:', error);
      }
    };

    checkWorkerSessionTimeout();
    const checkInterval = setInterval(checkWorkerSessionTimeout, WORKER_TIMEOUT_CHECK_INTERVAL_MS);

    return () => clearInterval(checkInterval);
  }, [user, logout]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/api/auth/me');
      setUser(response.data);
      syncReadOnlyStorage(response.data);
    } catch (error: any) {
      console.error('Error fetching user:', error);
      
      // Check if it's a JWT expiration error
      if (error.response?.status === 401 || 
          (error.response?.data?.detail && 
           (error.response.data.detail.includes('expired') || 
            error.response.data.detail.includes('JWT')))) {
        
        console.log('JWT expired, attempting to refresh token...');
        
        // Try to refresh the token before giving up
        const refreshed = await tryRefreshToken();
        if (!refreshed) {
          console.log('Token refresh failed, clearing auth state');
          localStorage.removeItem('worker_login_time');
          localStorage.removeItem(READ_ONLY_STORAGE_KEY);
          setUser(null);
        } else {
          console.log('Token refreshed successfully, retrying user fetch');
          // Token was refreshed, try fetching user again
          try {
            const response = await api.get('/api/auth/me');
            setUser(response.data);
          } catch (retryError: any) {
            console.error('Retry after token refresh failed:', retryError);
            localStorage.removeItem('worker_login_time');
            localStorage.removeItem(READ_ONLY_STORAGE_KEY);
            setUser(null);
          }
        }
      } else {
        // For other errors, just log them but don't clear the token
        console.error('Non-auth error fetching user:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const tryRefreshToken = async (): Promise<boolean> => {
    const { refreshAuthToken } = await import('../utils/authRefresh');
    return refreshAuthToken();
  };

  const login = async (username: string, password: string) => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/api/auth/login', formData, {
      headers: {
        'Content-Type': undefined, // Let browser set the correct multipart/form-data with boundary
      },
    });
    const { user: userData } = response.data;
    
    // Store login time for worker auto-logout (only workers are subject to session timeout)
    if (userData?.role === 'worker') {
      localStorage.setItem('worker_login_time', Date.now().toString());
    }
    
    setUser(userData);
    syncReadOnlyStorage(userData ?? null);
    // Record client local time for login tracking (non-blocking; do not fail login on error)
    try {
      // Avoid newer `dateStyle/timeStyle/timeZoneName` options that can throw
      // in some JS runtime environments.
      const clientLocalTime = new Date().toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
      await api.post('/api/auth/log-login', { client_local_time: clientLocalTime });
    } catch (_) {
      // Ignore; login succeeded
    }
    return userData; // Return user data for personalized messages
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await api.post('/api/auth/register', userData);
      const data = response.data;
      
      // Check if user needs email confirmation
      if (data.requires_confirmation) {
        // User needs to confirm email - just set user without token
        setUser(data.user);
        syncReadOnlyStorage(data.user ?? null);
        return;
      }
      
      // User is immediately authenticated - set token and user
      if (data.user) {
        if (data.user.role === 'worker') {
          localStorage.setItem('worker_login_time', Date.now().toString());
        }
        setUser(data.user);
        syncReadOnlyStorage(data.user ?? null);
      } else {
        // Fallback for backward compatibility
        setUser(data.user);
        syncReadOnlyStorage(data.user ?? null);
      }
    } catch (error: any) {
      console.error('Registration error:', error.response?.data || error.message);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, canAccessManagerFeatures: canAccessManagerFeatures(user), login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 
